/**
 * Loox Storefront API — custom product reviews UI (see docs/INTEGRATIONS-APPS.md).
 * API: https://developers.loox.app/documentation/
 */
const API_ORIGIN = "https://storefront-api.loox.io";

/** Storefront product-reviews list: comma-separated star levels (Loox docs). */
const LOOX_PRODUCT_REVIEWS_RATING_PARAM = "rating";

/** Which Loox list API returned this row (`store-reviews` vs `product-reviews`) — drives detail GET for reviewer names. */
const LOOX_REVIEW_ORIGIN = "_looxReviewOrigin";

/**
 * @param {unknown} review
 * @param {"store" | "product"} origin
 */
function tagReviewOrigin(review, origin) {
  if (!review || typeof review !== "object") return review;
  /** @type {Record<string, unknown>} */ (review)[LOOX_REVIEW_ORIGIN] = origin;
  return review;
}

/**
 * Shopify product id from a Loox review row (when present).
 * @param {object} review
 * @returns {string}
 */
function getProductIdFromReview(review) {
  if (!review || typeof review !== "object") return "";
  const p = /** @type {Record<string, unknown>} */ (review).product;
  if (!p || typeof p !== "object") return "";
  const id = /** @type {Record<string, unknown>} */ (p).id;
  return id != null && String(id).trim() !== "" ? String(id).trim() : "";
}

/**
 * @param {string} pathOrUrl
 */
function resolveLooxUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  if (pathOrUrl.startsWith("http")) return pathOrUrl;
  return `${API_ORIGIN}${pathOrUrl}`;
}

/**
 * @param {object} data
 * @returns {unknown[]}
 */
function extractReviews(data) {
  if (!data || typeof data !== "object") return [];
  if (Array.isArray(data.reviews)) return data.reviews;
  const inner = data.data;
  if (inner && typeof inner === "object" && Array.isArray(inner.reviews)) return inner.reviews;
  return [];
}

/**
 * Dedupe merged store + product lists: Loox may reuse the same `id` in both APIs — key by origin + id.
 * @param {unknown[]} reviews
 * @returns {object[]}
 */
function dedupeMergedLooxReviews(reviews) {
  const map = new Map();
  if (!Array.isArray(reviews)) return [];
  for (const r of reviews) {
    if (!r || typeof r !== "object") continue;
    const rec = /** @type {{ id?: unknown; _id?: unknown }} */ (r);
    const id = rec.id ?? rec._id;
    if (id == null || String(id).trim() === "") continue;
    const origin = /** @type {Record<string, unknown>} */ (r)[LOOX_REVIEW_ORIGIN];
    const originKey = origin === "store" || origin === "product" ? String(origin) : "na";
    const key = `${originKey}:${String(id)}`;
    if (map.has(key)) continue;
    map.set(key, /** @type {object} */ (r));
  }
  return [...map.values()];
}

/**
 * Single-review responses use `{ "review": { … } }` (see Loox Storefront API).
 * @param {unknown} data
 * @returns {object | null}
 */
function extractSingleReview(data) {
  if (!data || typeof data !== "object") return null;
  const rec = /** @type {Record<string, unknown>} */ (data);
  if (rec.review && typeof rec.review === "object") return /** @type {object} */ (rec.review);
  if (rec.data && typeof rec.data === "object") {
    const d = /** @type {Record<string, unknown>} */ (rec.data);
    if (d.review && typeof d.review === "object") return /** @type {object} */ (d.review);
    if (
      (typeof d.id === "string" || typeof d.id === "number") &&
      (Array.isArray(d.media) || d.body !== undefined || d.rating !== undefined)
    ) {
      return /** @type {object} */ (d);
    }
  }
  if (
    (typeof rec.id === "string" || typeof rec.id === "number") &&
    (Array.isArray(rec.media) || rec.body !== undefined || rec.rating !== undefined)
  ) {
    return /** @type {object} */ (data);
  }
  return null;
}

/**
 * Non-empty Loox media payload (detail endpoint may only add `images` / extra keys).
 * @param {object} review
 */
function reviewHasMediaPayload(review) {
  if (!review || typeof review !== "object") return false;
  const r = /** @type {Record<string, unknown>} */ (review);
  if (Array.isArray(r.media) && r.media.length > 0) return true;
  for (const key of ["images", "photos", "pictures", "customerPhotos"]) {
    const v = r[key];
    if (Array.isArray(v) && v.length > 0) return true;
  }
  return false;
}

/**
 * Merge only UGC media fields — never `reviewer` / `customerName` (product vs store detail IDs can collide).
 * @param {object} base
 * @param {object} detail
 */
function mergeLooxReviewMediaOnly(base, detail) {
  if (!base || typeof base !== "object" || !detail || typeof detail !== "object") return;
  const d = /** @type {Record<string, unknown>} */ (detail);
  const b = /** @type {Record<string, unknown>} */ (base);
  if (Array.isArray(d.media) && d.media.length > 0) b.media = d.media;
  for (const key of ["images", "photos", "pictures", "customerPhotos"]) {
    const v = d[key];
    if (Array.isArray(v) && v.length > 0 && (!Array.isArray(b[key]) || /** @type {unknown[]} */ (b[key]).length === 0)) {
      b[key] = v;
    }
  }
  for (const key of ["photoUrl", "photo_url", "photo", "image", "picture", "img", "thumbnail", "thumb"]) {
    if (d[key] != null && b[key] == null) b[key] = d[key];
  }
}

/**
 * Merge Storefront single-review GET onto list row (media often omitted from list).
 * @param {object} base
 * @param {object} detail
 */
function mergeLooxReviewDetail(base, detail) {
  if (!base || typeof base !== "object" || !detail || typeof detail !== "object") return;
  mergeLooxReviewMediaOnly(base, detail);
  const d = /** @type {Record<string, unknown>} */ (detail);
  const b = /** @type {Record<string, unknown>} */ (base);
  const dr = d.reviewer;
  if (dr && typeof dr === "object") {
    if (!b.reviewer || typeof b.reviewer !== "object") b.reviewer = {};
    Object.assign(/** @type {object} */ (b.reviewer), /** @type {object} */ (dr));
  }
  if (typeof d.customerName === "string" && d.customerName.trim() !== "" && !b.customerName) {
    b.customerName = d.customerName;
  }
}

/**
 * One Loox `media[]` item: `type` may be `photo` | `image` | `video`; prefer `url`, support `thumbnailUrl`.
 * @param {unknown} m
 * @returns {string | null}
 */
function firstDisplayableUrlFromMediaItem(m) {
  if (!m || typeof m !== "object") return null;
  const mo = /** @type {Record<string, unknown>} */ (m);
  const typeRaw = mo.type;
  const typeStr = typeof typeRaw === "string" ? typeRaw.toLowerCase() : "";
  const primary =
    pickReviewMediaUrl(mo.url) ||
    pickReviewMediaUrl(mo.src) ||
    pickReviewMediaUrl(mo.href) ||
    pickReviewMediaUrl(mo.original) ||
    pickReviewMediaUrl(mo.mediaUrl) ||
    pickReviewMediaUrl(mo.fullResolutionUrl) ||
    pickReviewMediaUrl(mo.largeUrl) ||
    pickReviewMediaUrl(mo.fullUrl) ||
    pickReviewMediaUrl(mo.previewUrl);
  const thumb = pickReviewMediaUrl(mo.thumbnailUrl);
  if (typeStr === "video") {
    return thumb || primary;
  }
  if (!typeStr || typeStr === "image" || typeStr === "photo" || typeStr.includes("picture")) {
    return primary || thumb;
  }
  return primary || thumb;
}

/**
 * GET `/storefront/v1/store/{publicStoreId}/product-reviews/{reviewId}` — Loox **storefront_getProductReview**.
 * Full payload (list rows may omit reviewer/media). {@link fetchSingleProductReview} returns the same row only when UGC media exists.
 * @see https://developers.loox.app/documentation/#Product-Reviews/operation/storefront_getProductReview
 */
async function fetchSingleProductReviewPayload(publicStoreId, productId, reviewId, locale, fetchOpts) {
  const segment = encodeURIComponent(reviewId);
  const basePath = `/storefront/v1/store/${publicStoreId}/product-reviews/${segment}`;

  /** @type {(() => string)[]} */
  const urlBuilders = [
    () => {
      const u = new URL(basePath, API_ORIGIN);
      u.searchParams.set("product_id", productId);
      if (locale) u.searchParams.set("locale", locale);
      return u.toString();
    },
    () => {
      const u = new URL(basePath, API_ORIGIN);
      u.searchParams.set("product_id", productId);
      return u.toString();
    },
    () => {
      const u = new URL(basePath, API_ORIGIN);
      if (locale) u.searchParams.set("locale", locale);
      return u.toString();
    },
  ];

  for (const build of urlBuilders) {
    let res;
    try {
      res = await fetch(build(), fetchOpts);
    } catch {
      continue;
    }
    if (!res.ok) continue;
    let data;
    try {
      /** @type {Record<string, unknown>} */
      data = await res.json();
    } catch {
      continue;
    }
    let rev = extractSingleReview(data);
    if (!rev && data && typeof data === "object" && reviewHasMediaPayload(/** @type {object} */ (data))) {
      rev = /** @type {object} */ (data);
    }
    if (rev) return rev;
  }
  return null;
}

async function fetchSingleProductReview(publicStoreId, productId, reviewId, locale, fetchOpts) {
  const rev = await fetchSingleProductReviewPayload(publicStoreId, productId, reviewId, locale, fetchOpts);
  if (rev && (reviewHasMediaPayload(rev) || getUGCImageUrl(rev))) return rev;
  return null;
}

/**
 * GET `/storefront/v1/store/{publicStoreId}/store-reviews/{reviewId}` — full payload (list rows may omit reviewer fields).
 * @param {string} publicStoreId
 * @param {string} reviewId
 * @param {string} locale
 * @param {object} fetchOpts
 * @returns {Promise<object | null>}
 */
async function fetchSingleStoreReviewPayload(publicStoreId, reviewId, locale, fetchOpts) {
  const segment = encodeURIComponent(String(reviewId));
  const basePath = `/storefront/v1/store/${publicStoreId}/store-reviews/${segment}`;

  /** @type {(() => string)[]} */
  const urlBuilders = [
    () => {
      const u = new URL(basePath, API_ORIGIN);
      if (locale) u.searchParams.set("locale", locale);
      return u.toString();
    },
    () => new URL(basePath, API_ORIGIN).toString(),
  ];

  for (const build of urlBuilders) {
    let res;
    try {
      res = await fetch(build(), fetchOpts);
    } catch {
      continue;
    }
    if (!res.ok) continue;
    let data;
    try {
      /** @type {Record<string, unknown>} */
      data = await res.json();
    } catch {
      continue;
    }
    let rev = extractSingleReview(data);
    if (!rev && data && typeof data === "object") {
      const o = /** @type {Record<string, unknown>} */ (data);
      if (
        (typeof o.id === "string" || typeof o.id === "number") &&
        (o.body !== undefined ||
          o.rating !== undefined ||
          o.reviewer !== undefined ||
          reviewHasMediaPayload(/** @type {object} */ (data)))
      ) {
        rev = /** @type {object} */ (data);
      }
    }
    if (rev) return rev;
  }
  return null;
}

/** Same endpoint as {@link fetchSingleStoreReviewPayload}; returns null unless the review has displayable UGC media (legacy helper for media enrichment). */
async function fetchSingleStoreReview(publicStoreId, reviewId, locale, fetchOpts) {
  const rev = await fetchSingleStoreReviewPayload(publicStoreId, reviewId, locale, fetchOpts);
  if (rev && (reviewHasMediaPayload(rev) || getUGCImageUrl(rev))) return rev;
  return null;
}

/**
 * Detail GET for enrichment: Loox may 404 `store-reviews/{id}` for rows that only exist on `product-reviews`
 * (list still renders). When a Shopify product id is known, try product detail first to avoid redundant 404s.
 * @param {"store" | "product" | unknown} origin
 * @param {string} publicStoreId
 * @param {string} reviewId
 * @param {string} locale
 * @param {object} fetchOpts
 * @param {string} [sectionProductId] PDP / section primary product (homepage merged lists)
 * @param {object} reviewRow
 * @returns {Promise<object | null>}
 */
async function fetchReviewDetailPayloadForEnrichment(
  origin,
  publicStoreId,
  reviewId,
  locale,
  fetchOpts,
  sectionProductId,
  reviewRow
) {
  const rid = String(reviewId);
  const pidFromRow = getProductIdFromReview(reviewRow);
  const pid =
    pidFromRow && String(pidFromRow).trim() !== ""
      ? String(pidFromRow).trim()
      : sectionProductId && String(sectionProductId).trim() !== ""
        ? String(sectionProductId).trim()
        : "";

  if (origin === "store") {
    if (pid) {
      const productFirst = await fetchSingleProductReviewPayload(publicStoreId, pid, rid, locale, fetchOpts);
      if (productFirst) return productFirst;
    }
    return fetchSingleStoreReviewPayload(publicStoreId, rid, locale, fetchOpts);
  }

  if (origin === "product") {
    if (pid) {
      return fetchSingleProductReviewPayload(publicStoreId, pid, rid, locale, fetchOpts);
    }
    return fetchSingleStoreReviewPayload(publicStoreId, rid, locale, fetchOpts);
  }

  if (pid) {
    const productTry = await fetchSingleProductReviewPayload(publicStoreId, pid, rid, locale, fetchOpts);
    if (productTry) return productTry;
  }
  return fetchSingleStoreReviewPayload(publicStoreId, rid, locale, fetchOpts);
}

async function enrichReviewsMediaFromDetail(reviews, publicStoreId, productId, locale, fetchOpts) {
  const targets = reviews.filter((r) => {
    if (!r || typeof r !== "object") return false;
    const media = Array.isArray(r.media) ? r.media : [];
    const id = /** @type {{ id?: unknown }} */ (r).id;
    return media.length === 0 && id != null && String(id).trim() !== "";
  });
  await Promise.all(
    targets.map(async (r) => {
      try {
        const rid = String(/** @type {{ id?: unknown }} */ (r).id);
        const origin = /** @type {Record<string, unknown>} */ (r)[LOOX_REVIEW_ORIGIN];

        /** @type {object | null} */
        let detail = null;

        if (origin === "store" || origin === "product") {
          const payload = await fetchReviewDetailPayloadForEnrichment(
            origin,
            publicStoreId,
            rid,
            locale,
            fetchOpts,
            productId,
            /** @type {object} */ (r)
          );
          if (payload && (reviewHasMediaPayload(payload) || getUGCImageUrl(/** @type {object} */ (payload)))) {
            detail = payload;
          }
        } else {
          const pid = getProductIdFromReview(r) || productId;
          if (pid && String(pid).trim() !== "") {
            detail = await fetchSingleProductReview(publicStoreId, pid, rid, locale, fetchOpts);
          }
          if (!detail) {
            const payload = await fetchSingleStoreReviewPayload(publicStoreId, rid, locale, fetchOpts);
            if (payload && (reviewHasMediaPayload(payload) || getUGCImageUrl(/** @type {object} */ (payload)))) {
              detail = payload;
            }
          }
        }

        if (detail) mergeLooxReviewMediaOnly(r, detail);
      } catch {
        /* ignore */
      }
    })
  );
}

/**
 * List responses may only send a generic `reviewer.name` (“Customer”); single-review GET includes full reviewer fields.
 * Rows must be tagged with {@link LOOX_REVIEW_ORIGIN} when merging store + product lists so we call the correct detail route.
 * @param {object[]} reviews
 * @param {string} publicStoreId
 * @param {string} locale
 * @param {object} fetchOpts
 * @param {string} [sectionProductId] Primary product id (PDP / section) for store-review detail fallback
 */
async function enrichReviewersFromDetail(reviews, publicStoreId, locale, fetchOpts, sectionProductId = "") {
  const targets = reviews.filter((r) => {
    if (!r || typeof r !== "object") return false;
    const nm = getReviewerDisplayName(r);
    return nm === "" || isGenericReviewerPlaceholder(nm);
  });
  await Promise.all(
    targets.map(async (r) => {
      try {
        const id = /** @type {{ id?: unknown }} */ (r).id;
        if (id == null || String(id).trim() === "") return;
        const origin = /** @type {Record<string, unknown>} */ (r)[LOOX_REVIEW_ORIGIN];

        /** @type {object | null} */
        let detail = null;

        if (origin === "product" || origin === "store") {
          detail = await fetchReviewDetailPayloadForEnrichment(
            origin,
            publicStoreId,
            String(id),
            locale,
            fetchOpts,
            sectionProductId,
            /** @type {object} */ (r)
          );
        } else {
          if (isProductScopedReview(r)) {
            const pid = getProductIdFromReview(r);
            if (pid) {
              detail = await fetchSingleProductReviewPayload(publicStoreId, pid, String(id), locale, fetchOpts);
            }
          }
          if (!detail) {
            detail = await fetchSingleStoreReviewPayload(publicStoreId, String(id), locale, fetchOpts);
          }
        }

        if (detail) mergeLooxReviewDetail(r, detail);
      } catch {
        /* ignore */
      }
    })
  );
}

/**
 * Normalize an API-provided URL (Loox may use strings, protocol-relative URLs, or objects).
 * @param {unknown} raw
 * @returns {string | null}
 */
/**
 * Loox sometimes returns `//images.loox.io//images.loox.io/uploads/…` (doubled host),
 * which stays broken after normalizing to https. Collapse to one CDN origin.
 * @param {string} url
 */
function fixLooxDoubledImageHost(url) {
  return url
    .replace(/^https:\/\/images\.loox\.io\/\/images\.loox\.io\//i, "https://images.loox.io/")
    .replace(/^http:\/\/images\.loox\.io\/\/images\.loox\.io\//i, "https://images.loox.io/");
}

/**
 * @param {unknown} raw
 */
function pickReviewMediaUrl(raw) {
  if (raw == null) return null;
  const s = typeof raw === "string" ? raw.trim() : "";
  if (!s) return null;
  let out = null;
  if (s.startsWith("https://") || s.startsWith("http://")) out = s;
  else if (s.startsWith("//")) out = `https:${s}`;
  if (!out) return null;
  return fixLooxDoubledImageHost(out);
}

/**
 * Customer-submitted images only (Loox `media`, `images`, etc.).
 * List responses may omit `media`; {@link enrichReviewsMediaFromDetail} merges single-review `media[]`.
 * @param {object} review
 * @returns {string | null}
 */
function getUGCImageUrl(review) {
  if (!review || typeof review !== "object") return null;

  /** @type {Record<string, unknown>} */
  const r = review;

  for (const key of ["photoUrl", "photo_url", "photo", "image", "picture", "img", "thumbnail", "thumb"]) {
    const u = pickReviewMediaUrl(r[key]);
    if (u) return u;
  }

  /** @param {unknown[]} arr */
  const firstFromUrlArray = (arr) => {
    if (!Array.isArray(arr)) return null;
    for (const item of arr) {
      if (typeof item === "string") {
        const u = pickReviewMediaUrl(item);
        if (u) return u;
      }
      if (item && typeof item === "object") {
        const o = /** @type {Record<string, unknown>} */ (item);
        const u =
          pickReviewMediaUrl(o.url) ||
          pickReviewMediaUrl(o.src) ||
          pickReviewMediaUrl(o.href) ||
          pickReviewMediaUrl(o.original) ||
          pickReviewMediaUrl(o.thumbnailUrl);
        if (u) return u;
      }
    }
    return null;
  };

  const fromImages = firstFromUrlArray(/** @type {unknown} */ (r.images));
  if (fromImages) return fromImages;

  for (const key of ["photos", "pictures", "picture_urls"]) {
    const u = firstFromUrlArray(/** @type {unknown} */ (r[key]));
    if (u) return u;
  }

  const media = Array.isArray(r.media) ? r.media : [];
  for (const m of media) {
    if (typeof m === "string") {
      const u = pickReviewMediaUrl(m);
      if (u) return u;
      continue;
    }
    const url = firstDisplayableUrlFromMediaItem(m);
    if (url) return url;
  }

  return null;
}

/**
 * Customer photo for card thumb + modal visual only — never `review.product.imageUrl` (catalog image).
 * @param {object} review
 * @returns {string | null}
 */
function getFirstReviewImageUrl(review) {
  return getUGCImageUrl(review);
}

/**
 * True only when the review has UGC photos (used for sort — not catalog fallback).
 * @param {object} review
 * @returns {boolean}
 */
function reviewHasPhoto(review) {
  return Boolean(getUGCImageUrl(review));
}

/**
 * @param {object} review
 * @returns {number}
 */
function reviewTimestamp(review) {
  const raw = review.date || review.createdAt;
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : 0;
}

/**
 * @param {unknown} rating
 * @returns {number}
 */
function normalizedStarRating(rating) {
  const n = Math.round(Number(rating));
  if (!Number.isFinite(n)) return -1;
  return Math.min(5, Math.max(1, n));
}

/**
 * Parses theme setting (comma-separated 1–5) for API and filtering.
 * @param {string} raw
 * @returns {{ allowed: Set<number>, queryValue: string }}
 */
function parseRatingsFilter(raw) {
  const s = typeof raw === "string" ? raw.trim() : "";
  const nums = new Set();
  if (s) {
    for (const part of s.split(",")) {
      const n = parseInt(String(part).trim(), 10);
      if (n >= 1 && n <= 5) nums.add(n);
    }
  }
  if (nums.size === 0) {
    nums.add(4);
    nums.add(5);
  }
  const queryValue = [...nums].sort((a, b) => a - b).join(",");
  return { allowed: nums, queryValue };
}

/**
 * Filter to allowed star levels; order: higher stars first, then photos, then newest.
 * @param {unknown[]} reviews
 * @param {Set<number>} allowedRatings
 * @returns {object[]}
 */
function sortAndFilterReviews(reviews, allowedRatings) {
  const list = Array.isArray(reviews) ? reviews : [];
  const allowed =
    allowedRatings instanceof Set && allowedRatings.size > 0 ? allowedRatings : new Set([4, 5]);
  const filtered = list.filter((r) => {
    if (!r || typeof r !== "object") return false;
    const n = normalizedStarRating(/** @type {{ rating?: unknown }} */ (r).rating);
    return n >= 1 && allowed.has(n);
  });
  filtered.sort((a, b) => {
    const ra = normalizedStarRating(a.rating);
    const rb = normalizedStarRating(b.rating);
    if (rb !== ra) return rb - ra;
    const pa = reviewHasPhoto(a) ? 1 : 0;
    const pb = reviewHasPhoto(b) ? 1 : 0;
    if (pb !== pa) return pb - pa;
    return reviewTimestamp(b) - reviewTimestamp(a);
  });
  return filtered;
}

/** Material-style 24×24 star path (matches theme SVG sizing in CSS) */
const LOOX_STAR_PATH =
  "M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27z";

/** Verified badge icon (18×18, #DF552D) */
const LOOX_VERIFIED_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true" focusable="false"><path d="M9 1.6875C7.55375 1.6875 6.13993 2.11637 4.9374 2.91988C3.73486 3.72338 2.7976 4.86544 2.24413 6.20163C1.69067 7.53778 1.54586 9.00809 1.82801 10.4266C2.11017 11.8451 2.80661 13.148 3.82929 14.1707C4.85196 15.1934 6.15492 15.8899 7.57343 16.172C8.99192 16.4542 10.4622 16.3093 11.7984 15.7558C13.1346 15.2024 14.2766 14.2651 15.0801 13.0626C15.8837 11.8601 16.3125 10.4463 16.3125 9C16.3105 7.0612 15.5393 5.20246 14.1685 3.83155C12.7975 2.46063 10.9388 1.68955 9 1.6875ZM12.2105 7.71047L8.27297 11.648C8.22073 11.7003 8.15871 11.7418 8.09044 11.7701C8.0221 11.7984 7.9489 11.8129 7.875 11.8129C7.8011 11.8129 7.72791 11.7984 7.65957 11.7701C7.59129 11.7418 7.52928 11.7003 7.47703 11.648L5.78953 9.96047C5.68399 9.85493 5.62469 9.71177 5.62469 9.5625C5.62469 9.41323 5.68399 9.27007 5.78953 9.16453C5.89508 9.05899 6.03824 8.99972 6.1875 8.99972C6.33677 8.99972 6.47993 9.05899 6.58547 9.16453L7.875 10.4548L11.4145 6.91453C11.4668 6.86227 11.5289 6.82081 11.5971 6.79253C11.6654 6.76425 11.7386 6.74969 11.8125 6.74969C11.8864 6.74969 11.9596 6.76425 12.0279 6.79253C12.0961 6.82081 12.1582 6.86227 12.2105 6.91453C12.2627 6.96679 12.3042 7.02884 12.3325 7.09713C12.3607 7.16541 12.3753 7.2386 12.3753 7.3125C12.3753 7.3864 12.3607 7.45959 12.3325 7.52787C12.3042 7.59614 12.2627 7.65823 12.2105 7.71047Z" fill="#DF552D"/></svg>`;

/**
 * Whole stars 1–5 matching rounded rating (same as typical Loox widgets).
 * @param {number} rating
 * @param {string} [uniqueSuffix] aria uniqueness when multiple cards
 */
function renderStars(rating, uniqueSuffix = "") {
  const raw = Number(rating);
  const filled = Math.min(5, Math.max(0, Math.round(Number.isFinite(raw) ? raw : 0)));
  const max = 5;
  const labelId = uniqueSuffix ? `loox-sfr-rating-${uniqueSuffix}` : "loox-sfr-rating";
  let html = `<span class="loox-sfr__stars loox-sfr__stars--svg" role="img" aria-labelledby="${labelId}">`;
  html += `<span id="${labelId}" class="visually-hidden">${filled} out of 5 stars</span>`;
  for (let i = 1; i <= max; i += 1) {
    const isActive = i <= filled;
    if (isActive) {
      html += `<svg class="loox-sfr__star-svg is-active" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path fill="#000000" d="${LOOX_STAR_PATH}"/></svg>`;
    } else {
      html += `<svg class="loox-sfr__star-svg" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path fill="none" stroke="#000000" stroke-width="1.2" stroke-linejoin="round" d="${LOOX_STAR_PATH}"/></svg>`;
    }
  }
  html += "</span>";
  return html;
}

/**
 * DD/MM/YY (UK order) regardless of storefront locale.
 * @param {string} iso
 */
function formatReviewDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    }).format(d);
  } catch {
    return "";
  }
}

/**
 * Stable DOM / Map key — include API origin when present so store vs product rows never collide.
 * @param {object} review
 * @param {number} idx
 */
function getReviewKey(review, idx) {
  const id = review.id ?? review._id;
  if (id != null && String(id).length > 0) {
    const origin = /** @type {Record<string, unknown>} */ (review)[LOOX_REVIEW_ORIGIN];
    if (origin === "store" || origin === "product") return `${String(origin)}:${String(id)}`;
    return String(id);
  }
  return `loox-sfr-idx-${idx}`;
}

/**
 * Loox uses product-shaped `reviewer.name`; store reviews may use first/last only or a generic label.
 * @param {string} name
 */
function isGenericReviewerPlaceholder(name) {
  const s = typeof name === "string" ? name.trim().toLowerCase() : "";
  return s === "" || s === "customer" || s === "anonymous" || s === "anonymous customer";
}

/**
 * Pick display label from one Loox `reviewer` object (not an array).
 * @param {unknown} o
 * @returns {string}
 */
function nameFromLooxReviewerObject(o) {
  if (!o || typeof o !== "object" || Array.isArray(o)) return "";
  const rv = /** @type {Record<string, unknown>} */ (o);
  const clean = (/** @type {unknown} */ v) => {
    if (v == null || v === "") return "";
    const s = typeof v === "string" ? v.trim() : String(v).trim();
    return s && !isGenericReviewerPlaceholder(s) ? s : "";
  };

  const primary = clean(rv.name);
  if (primary) return primary;

  const display =
    typeof rv.displayName === "string"
      ? rv.displayName.trim()
      : typeof rv.fullName === "string"
        ? rv.fullName.trim()
        : "";
  if (display && !isGenericReviewerPlaceholder(display)) return display;

  const fn = typeof rv.firstName === "string" ? rv.firstName.trim() : "";
  const ln = typeof rv.lastName === "string" ? rv.lastName.trim() : "";
  const combined = [fn, ln].filter(Boolean).join(" ");
  if (combined) return combined;

  const nick = typeof rv.nickname === "string" ? rv.nickname.trim() : "";
  if (nick && !isGenericReviewerPlaceholder(nick)) return nick;
  return "";
}

/**
 * Loox `reviewer` field: string, `{ name }`, or rarely an array — `typeof x === "object"` is true for arrays.
 * @param {unknown} rev
 * @returns {string}
 */
function nameFromLooxReviewerValue(rev) {
  if (rev == null) return "";
  if (typeof rev === "string") {
    const s = rev.trim();
    return s && !isGenericReviewerPlaceholder(s) ? s : "";
  }
  if (Array.isArray(rev)) {
    for (const item of rev) {
      const n = nameFromLooxReviewerObject(item);
      if (n) return n;
    }
    return "";
  }
  if (typeof rev === "object") {
    return nameFromLooxReviewerObject(rev);
  }
  return "";
}

/**
 * Prefer nested `reviewer` (Loox Storefront schema); then top-level fallbacks.
 * @param {object} review
 * @returns {string}
 */
function getReviewerDisplayName(review) {
  if (!review || typeof review !== "object") return "";
  const r = /** @type {Record<string, unknown>} */ (review);

  const fromReviewerField = nameFromLooxReviewerValue(r.reviewer);
  if (fromReviewerField) return fromReviewerField;

  const reviewerNameTop = typeof r.reviewerName === "string" ? r.reviewerName.trim() : "";
  if (reviewerNameTop && !isGenericReviewerPlaceholder(reviewerNameTop)) return reviewerNameTop;

  const cust = r.customer;
  if (cust && typeof cust === "object") {
    const c = /** @type {Record<string, unknown>} */ (cust);
    const cfn = typeof c.firstName === "string" ? c.firstName.trim() : "";
    const cln = typeof c.lastName === "string" ? c.lastName.trim() : "";
    const comb = [cfn, cln].filter(Boolean).join(" ");
    const cname = typeof c.name === "string" ? c.name.trim() : "";
    if (comb) return comb;
    if (cname && !isGenericReviewerPlaceholder(cname)) return cname;
  }

  const cn = typeof r.customerName === "string" ? r.customerName.trim() : "";
  if (cn && !isGenericReviewerPlaceholder(cn)) return cn;
  const auth = typeof r.author === "string" ? r.author.trim() : "";
  if (auth && !isGenericReviewerPlaceholder(auth)) return auth;
  return "";
}

/**
 * @param {object} review
 * @param {{ reviewerFallback?: string }} strings
 */
function formatReviewerDisplay(review, strings) {
  const raw = getReviewerDisplayName(review);
  if (raw) return raw;
  const fb = (strings.reviewerFallback || "").trim();
  return fb || "Customer";
}

/**
 * Product-review rows include a `product` object with a real id; ignore empty `product: {}` from store lists.
 * @param {object} review
 */
function isProductScopedReview(review) {
  return getProductIdFromReview(review) !== "";
}

/**
 * Store rows often omit `verified`; product rows use `verified` / purchase flags.
 * @param {object} review
 * @returns {boolean}
 */
function isReviewVerified(review) {
  if (!review || typeof review !== "object") return false;
  if (!isProductScopedReview(review)) {
    return true;
  }
  const r = /** @type {Record<string, unknown>} */ (review);
  if (r.verified === true) return true;
  if (r.verifiedPurchase === true) return true;
  if (r.isVerified === true) return true;
  if (r.verifiedBuyer === true) return true;
  const vs = r.verificationStatus;
  if (typeof vs === "string" && vs.toLowerCase() === "verified") return true;
  return false;
}

/**
 * @param {object} review
 * @param {object} strings
 * @param {number} cardIndex
 */
function renderReviewCard(review, strings, cardIndex) {
  const key = getReviewKey(review, cardIndex);
  const thumbUrl = getFirstReviewImageUrl(review);
  const thumbHtml = thumbUrl
    ? `<div class="loox-sfr__card-thumb"><img src="${encodeURI(thumbUrl)}" alt="" width="200" height="267" loading="lazy" decoding="async"></div>`
    : "";

  const verified =
    isReviewVerified(review)
      ? `<span class="loox-sfr__verified"><span class="loox-sfr__verified-icon">${LOOX_VERIFIED_ICON_SVG}</span><span class="loox-sfr__verified-text">${escapeHtml(strings.verified)}</span></span>`
      : "";

  const reviewerName = formatReviewerDisplay(review, strings);

  return `
    <article
      class="loox-sfr__card loox-sfr__card--interactive"
      role="button"
      tabindex="0"
      data-review-id="${escapeHtml(key)}"
      aria-label="${escapeHtml(strings.openReviewDetails)}"
    >
      <div class="loox-sfr__card-inner">
        <div class="loox-sfr__card-column loox-sfr__card-column--main">
          <div class="loox-sfr__card-top">
            <div class="loox-sfr__rating-wrap">${renderStars(review.rating, key)}</div>
            <time class="loox-sfr__date loox-sfr__date--top" datetime="${escapeHtml(review.date || review.createdAt || "")}">${escapeHtml(formatReviewDate(review.date || review.createdAt))}</time>
          </div>
          <div class="loox-sfr__author-block">
            <cite class="loox-sfr__name">${escapeHtml(reviewerName)}</cite>
            ${verified}
          </div>
          <blockquote class="loox-sfr__body loox-sfr__body--card">${escapeHtml(review.body || "").replace(/\n/g, "<br>")}</blockquote>
        </div>
        ${thumbHtml}
      </div>
    </article>
  `;
}

/**
 * @param {string} text
 */
function escapeHtml(text) {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

class LooxStorefrontReviews extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {Map<string, object>} */
  #reviewsById = new Map();

  /** @type {HTMLDialogElement | null} */
  #dialogEl = null;

  connectedCallback() {
    this.#abort = new AbortController();
    this.#init().catch(() => {});
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  #setReviewsIdle() {
    this.setAttribute("aria-busy", "false");
  }

  /**
   * Rating summary slot in section toolbar (grid layout only).
   * @returns {HTMLElement | null}
   */
  #toolbarSummarySlot() {
    const root = this.closest("[data-loox-sfr-section]");
    return /** @type {HTMLElement | null} */ (root?.querySelector("[data-loox-sfr-toolbar-summary]") ?? null);
  }

  #clearToolbarSummarySlot() {
    const slot = this.#toolbarSummarySlot();
    if (slot) slot.innerHTML = "";
  }

  /**
   * Grid: bottomline is rendered in the toolbar next to Write a review. Carousel: above cards inside this element.
   * @param {string} layout
   * @param {string} bottomlineHtml
   * @param {string} restInnerHtml grid/carousel body plus optional load-more (no bottomline)
   */
  #applyBottomlinePlacement(layout, bottomlineHtml, restInnerHtml) {
    const slot = this.#toolbarSummarySlot();
    if (layout === "grid" && slot) {
      slot.innerHTML = bottomlineHtml;
      this.innerHTML = restInnerHtml;
      return;
    }
    this.#clearToolbarSummarySlot();
    this.innerHTML = bottomlineHtml + restInnerHtml;
  }

  /**
   * @param {object} strings
   */
  #ensureDialog(strings) {
    if (this.#dialogEl) {
      if (!this.#dialogEl.isConnected) this.appendChild(this.#dialogEl);
      return this.#dialogEl;
    }

    const dlg = document.createElement("dialog");
    dlg.className = "loox-sfr__modal dialog";
    dlg.setAttribute("aria-modal", "true");
    dlg.innerHTML = `
      <div class="loox-sfr__modal-header">
        <button type="button" class="loox-sfr__modal-close btn btn--plain" aria-label="${escapeHtml(strings.closeDialog)}">
          <span aria-hidden="true">×</span>
        </button>
      </div>
      <div class="loox-sfr__modal-panel">
        <div class="loox-sfr__modal-visual">
          <div class="loox-sfr__modal-visual-inner"></div>
        </div>
        <div class="loox-sfr__modal-main"></div>
      </div>
    `;

    const close = () => {
      dlg.close();
      dlg.querySelector(".loox-sfr__modal-visual-inner")?.replaceChildren();
      const main = dlg.querySelector(".loox-sfr__modal-main");
      if (main) main.innerHTML = "";
    };

    dlg.querySelector(".loox-sfr__modal-close")?.addEventListener("click", close);

    dlg.addEventListener("click", (e) => {
      if (e.target === dlg) close();
    });

    dlg.addEventListener("close", () => {
      document.body.style.overflow = "";
    });

    this.#dialogEl = /** @type {HTMLDialogElement} */ (dlg);
    return dlg;
  }

  /**
   * @param {object} review
   * @param {object} strings
   * @param {string} reviewKey
   */
  #fillDialog(review, strings, reviewKey) {
    const dlg = this.#dialogEl;
    if (!dlg) return;

    const visualInner = dlg.querySelector(".loox-sfr__modal-visual-inner");
    const main = dlg.querySelector(".loox-sfr__modal-main");
    if (!visualInner || !main) return;

    const imgUrl = getFirstReviewImageUrl(review);

    if (imgUrl) {
      visualInner.innerHTML = `<img class="loox-sfr__modal-photo" src="${encodeURI(imgUrl)}" alt="" width="800" height="800" loading="eager" decoding="async">`;
      dlg.classList.remove("loox-sfr__modal--no-photo");
    } else {
      visualInner.innerHTML = "";
      dlg.classList.add("loox-sfr__modal--no-photo");
    }

    const verified =
      isReviewVerified(review)
        ? `<span class="loox-sfr__verified"><span class="loox-sfr__verified-icon">${LOOX_VERIFIED_ICON_SVG}</span><span class="loox-sfr__verified-text">${escapeHtml(strings.verified)}</span></span>`
        : "";

    const reviewerName = formatReviewerDisplay(review, strings);

    main.innerHTML = `
      <div class="loox-sfr__card-column loox-sfr__card-column--main loox-sfr__modal-column">
        <div class="loox-sfr__card-top">
          <div class="loox-sfr__rating-wrap">${renderStars(review.rating, `modal-${reviewKey}`)}</div>
          <time class="loox-sfr__date loox-sfr__date--top" datetime="${escapeHtml(review.date || review.createdAt || "")}">${escapeHtml(formatReviewDate(review.date || review.createdAt))}</time>
        </div>
        <div class="loox-sfr__author-block">
          <cite class="loox-sfr__name">${escapeHtml(reviewerName)}</cite>
          ${verified}
        </div>
        <blockquote class="loox-sfr__body loox-sfr__modal-quote">${escapeHtml(review.body || "").replace(/\n/g, "<br>")}</blockquote>
      </div>
    `;
  }

  /**
   * Lazy-fetch single review if list row still has no UGC media (matches storefront_getProductReview).
   * @param {string} id
   * @param {object} strings
   */
  async #openDialog(id, strings) {
    const review = this.#reviewsById.get(id);
    if (!review) return;

    const publicStoreId = this.dataset.publicStoreId?.trim();
    const productId = this.dataset.productId?.trim();
    const locale = (this.dataset.locale || "").trim();

    if (!getUGCImageUrl(review) && publicStoreId && review.id != null) {
      const fetchOpts = {
        signal: this.#abort?.signal,
        credentials: "omit",
        mode: "cors",
        headers: { Accept: "application/json" },
      };
      const rid = String(review.id);
      const origin = /** @type {Record<string, unknown>} */ (review)[LOOX_REVIEW_ORIGIN];

      /** @type {object | null} */
      let detail = null;

      if (origin === "store") {
        const payload = await fetchSingleStoreReviewPayload(publicStoreId, rid, locale, fetchOpts);
        if (payload && (reviewHasMediaPayload(payload) || getUGCImageUrl(/** @type {object} */ (payload)))) {
          detail = payload;
        }
      } else if (origin === "product") {
        const pid = getProductIdFromReview(review) || (productId || "").trim();
        if (pid) {
          detail = await fetchSingleProductReview(publicStoreId, pid, rid, locale, fetchOpts);
        }
      } else {
        const pid = getProductIdFromReview(review) || (productId || "").trim();
        if (pid) {
          detail = await fetchSingleProductReview(publicStoreId, pid, rid, locale, fetchOpts);
        }
        if (!detail) {
          detail = await fetchSingleStoreReview(publicStoreId, rid, locale, fetchOpts);
        }
      }

      if (detail) mergeLooxReviewMediaOnly(review, detail);
      const thumbSrc = getFirstReviewImageUrl(review);
      if (thumbSrc) {
        const escId = typeof CSS !== "undefined" && typeof CSS.escape === "function" ? CSS.escape(id) : id.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
        const cardEl = this.querySelector(`[data-review-id="${escId}"]`);
        if (cardEl) {
          let thumbEl = cardEl.querySelector(".loox-sfr__card-thumb");
          const inner = cardEl.querySelector(".loox-sfr__card-inner");
          const imgHtml = `<img src="${encodeURI(thumbSrc)}" alt="" width="200" height="267" loading="lazy" decoding="async">`;
          if (thumbEl) thumbEl.innerHTML = imgHtml;
          else if (inner)
            inner.insertAdjacentHTML("beforeend", `<div class="loox-sfr__card-thumb">${imgHtml}</div>`);
        }
      }
    }

    const dlg = this.#ensureDialog(strings);
    this.#fillDialog(review, strings, id);
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    document.body.style.overflow = "hidden";
    if (typeof dlg.showModal === "function") {
      dlg.showModal();
    }
    const restoreScroll = () => window.scrollTo(scrollX, scrollY);
    restoreScroll();
    requestAnimationFrame(restoreScroll);
  }

  /**
   * @param {object} strings
   */
  #bindCardInteractions(strings) {
    const dlg = this.#ensureDialog(strings);
    if (!dlg.isConnected) this.appendChild(dlg);

    this.querySelectorAll(".loox-sfr__card--interactive[data-review-id]").forEach((card) => {
      card.addEventListener("click", () => {
        const id = card.getAttribute("data-review-id");
        if (id) void this.#openDialog(id, strings);
      });

      card.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          const id = card.getAttribute("data-review-id");
          if (id) void this.#openDialog(id, strings);
        }
      });
    });
  }

  /**
   * @param {unknown[]} reviews
   */
  #indexReviews(reviews) {
    this.#reviewsById.clear();
    reviews.forEach((r, i) => {
      if (r && typeof r === "object") {
        this.#reviewsById.set(getReviewKey(r, i), r);
      }
    });
  }

  /**
   * Carousel toolbar + horizontal scroll (homepage layout).
   * @param {Element} root `[data-loox-sfr-section]`
   */
  #bindCarousel(root) {
    const track = this.querySelector(".loox-sfr__carousel-track");
    const scroller = this.querySelector(".loox-sfr__carousel-viewport");
    const prev = root.querySelector("[data-loox-carousel-prev]");
    const next = root.querySelector("[data-loox-carousel-next]");
    const counter = root.querySelector("[data-loox-carousel-counter]");
    const progress = root.querySelector("[data-loox-carousel-progress]");
    if (!track || !scroller) return;

    const cardEls = () => track.querySelectorAll(".loox-sfr__card");

    const update = () => {
      const nodes = cardEls();
      const total = nodes.length;
      if (total === 0) return;
      const first = nodes[0];
      if (!(first instanceof HTMLElement)) return;
      const gapRaw = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || "16");
      const gap = Number.isFinite(gapRaw) ? gapRaw : 16;
      const stride = first.offsetWidth + gap;
      if (!(stride > 0)) return;
      const perView = Math.max(1, Math.min(total, Math.floor(scroller.clientWidth / stride)));
      const totalPages = Math.max(1, Math.ceil(total / perView));
      const leftIdx = Math.round(scroller.scrollLeft / stride);
      const clampedIdx = Math.min(Math.max(0, leftIdx), total - 1);
      const currentPage = Math.min(totalPages - 1, Math.floor(clampedIdx / perView));
      if (counter) counter.textContent = `${currentPage + 1} / ${totalPages}`;
      if (progress) {
        const pct = totalPages > 0 ? ((currentPage + 1) / totalPages) * 100 : 0;
        progress.style.width = `${Math.min(100, pct)}%`;
      }
    };

    scroller.addEventListener("scroll", () => update(), { passive: true });
    window.addEventListener("resize", () => update());

    const scrollByDir = (dir) => {
      const nodes = cardEls();
      const first = nodes[0];
      if (!(first instanceof HTMLElement)) return;
      const gapRaw = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || "16");
      const gap = Number.isFinite(gapRaw) ? gapRaw : 16;
      const stride = first.offsetWidth + gap;
      if (!(stride > 0)) return;
      const perView = Math.max(1, Math.floor(scroller.clientWidth / stride));
      const delta = dir * perView * stride;
      const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      const next = Math.max(0, Math.min(maxScroll, scroller.scrollLeft + delta));
      scroller.scrollTo({ left: next, behavior: "smooth" });
    };

    prev?.addEventListener("click", () => scrollByDir(-1));
    next?.addEventListener("click", () => scrollByDir(1));

    requestAnimationFrame(() => update());
  }

  async #init() {
    const publicStoreId = this.dataset.publicStoreId?.trim();
    if (!publicStoreId) {
      this.#renderMissingConfig();
      return;
    }

    if (!/^[\w.-]+$/.test(publicStoreId)) {
      this.#renderError(this.dataset.strError || "Invalid store ID");
      return;
    }

    const scope = (this.dataset.reviewScope || "product").trim();
    const layout = (this.dataset.layout || "grid").trim();
    const primaryProductId = (this.dataset.productId || "").trim();
    const productIdsRaw = (this.dataset.productIds || "").trim();

    const limit = Math.min(50, Math.max(1, parseInt(this.dataset.limit || "12", 10) || 12));
    const direction = this.dataset.direction || "desc";
    const sort = (this.dataset.sort || "date").trim();
    const { allowed: allowedRatings, queryValue: ratingsQueryValue } = parseRatingsFilter(
      this.dataset.ratingsFilter || ""
    );
    const locale = (this.dataset.locale || "").trim();
    const showBottomline = this.dataset.showBottomline === "true";
    const enableLoadMore = this.dataset.enableLoadMore === "true";
    const reviewsLabel = (this.dataset.bottomlineLabel || "reviews").trim();
    const isDesignMode = this.dataset.designMode === "true";
    const needsCollectionHint = (this.dataset.strNeedsCollection || "").trim();

    const strings = {
      error: this.dataset.strError || "",
      loadMore: this.dataset.strLoadMore || "",
      verified: this.dataset.strVerified || "",
      apiCors: (this.dataset.strApiCors || "").trim(),
      closeDialog: (this.dataset.strCloseDialog || "Close").trim(),
      openReviewDetails: (this.dataset.strOpenReviewDetails || "View full review").trim(),
      reviewerFallback: (this.dataset.strReviewerFallback || "").trim(),
    };

    const fetchOpts = {
      signal: this.#abort?.signal,
      credentials: "omit",
      mode: "cors",
      headers: { Accept: "application/json" },
    };

    const singleProductList = scope === "product" && Boolean(primaryProductId) && !productIdsRaw;

    const needsProductSource = scope === "product";
    const hasProductSource =
      Boolean(primaryProductId) ||
      productIdsRaw.split(",").some((s) => s.trim().length > 0);

    if (needsProductSource && !hasProductSource) {
      this.#clearToolbarSummarySlot();
      if (isDesignMode && needsCollectionHint) {
        this.innerHTML = `<div class="loox-sfr__editor-hint">${escapeHtml(needsCollectionHint)}</div>`;
      } else {
        const empty = this.dataset.emptyMessage?.trim();
        this.innerHTML = `<div class="loox-sfr__empty">${empty ? escapeHtml(empty).replace(/\n/g, "<br>") : ""}</div>`;
      }
      this.#setReviewsIdle();
      return;
    }

    try {
      if (singleProductList && primaryProductId) {
        const productId = primaryProductId;
        const listUrl = new URL(
          `/storefront/v1/store/${publicStoreId}/product-reviews`,
          API_ORIGIN
        );
        listUrl.searchParams.set("product_id", productId);
        listUrl.searchParams.set("limit", String(limit));
        listUrl.searchParams.set("sort", sort);
        listUrl.searchParams.set("direction", direction);
        if (ratingsQueryValue) {
          listUrl.searchParams.set(LOOX_PRODUCT_REVIEWS_RATING_PARAM, ratingsQueryValue);
        }
        if (locale) listUrl.searchParams.set("locale", locale);

        const requests = [fetch(listUrl.toString(), fetchOpts)];
        if (showBottomline) {
          const blUrl = new URL(
            `/storefront/v1/store/${publicStoreId}/product-reviews/bottomline/${productId}`,
            API_ORIGIN
          );
          if (locale) blUrl.searchParams.set("locale", locale);
          requests.push(fetch(blUrl.toString(), fetchOpts));
        }

        const responses = await Promise.all(requests);
        const listRes = responses[0];

        if (!listRes.ok) {
          let detail = "";
          try {
            const errBody = await listRes.clone().json();
            if (errBody && typeof errBody === "object" && errBody.message) detail = String(errBody.message);
          } catch {
            try {
              detail = (await listRes.clone().text()).slice(0, 200);
            } catch {
              /* ignore */
            }
          }
          this.#renderFetchError(listRes.status, detail, strings, isDesignMode);
          return;
        }

        /** @type {Record<string, unknown>} */
        const data = await listRes.json();
        let reviews = extractReviews(data);

        if (reviews.length === 0 && locale) {
          const retryUrl = new URL(listUrl.toString());
          retryUrl.searchParams.delete("locale");
          const retryRes = await fetch(retryUrl.toString(), fetchOpts);
          if (retryRes.ok) {
            const retryData = await retryRes.json();
            reviews = extractReviews(retryData);
            Object.assign(data, retryData);
          }
        }

        /** @type {object[]} */
        let sorted = sortAndFilterReviews(reviews, allowedRatings);

        await enrichReviewersFromDetail(sorted, publicStoreId, locale, fetchOpts, productId);
        await enrichReviewsMediaFromDetail(sorted, publicStoreId, productId, locale, fetchOpts);
        sorted = sortAndFilterReviews(sorted, allowedRatings);

        let bottomlineHtml = "";
        if (showBottomline && responses[1]?.ok) {
          /** @type {{ averageRating?: number, totalReviews?: number }} */
          const bl = await responses[1].json();
          const avg = bl.averageRating != null ? Number(bl.averageRating).toFixed(1) : "";
          const total = bl.totalReviews != null ? bl.totalReviews : "";
          if (avg !== "" || total !== "") {
            bottomlineHtml = `<div class="loox-sfr__bottomline"><span class="loox-sfr__bottomline-rating">${escapeHtml(String(avg))}</span><span class="loox-sfr__bottomline-sep">·</span><span class="loox-sfr__bottomline-count">${escapeHtml(String(total))} ${escapeHtml(reviewsLabel)}</span></div>`;
          }
        }

        if (sorted.length === 0) {
          this.#clearToolbarSummarySlot();
          const empty = this.dataset.emptyMessage?.trim();
          this.innerHTML = `<div class="loox-sfr__empty">${empty ? escapeHtml(empty).replace(/\n/g, "<br>") : ""}</div>`;
          this.#setReviewsIdle();
          return;
        }

        this.#indexReviews(sorted);
        const cards = sorted.map((r, i) => renderReviewCard(r, strings, i)).join("");
        const body =
          layout === "carousel"
            ? `<div class="loox-sfr__carousel-viewport"><div class="loox-sfr__carousel-track">${cards}</div></div>`
            : `<div class="loox-sfr__grid">${cards}</div>`;
        const loadMoreSection =
          enableLoadMore && data.pagination?.hasMore && data.pagination?.nextPageUrl
            ? `<div class="loox-sfr__load-more-wrap"><button type="button" class="btn btn--secondary btn--medium loox-sfr__load-more">${escapeHtml(strings.loadMore)}</button></div>`
            : "";

        const restInner = `${body}${loadMoreSection}`;
        this.#applyBottomlinePlacement(layout, bottomlineHtml, restInner);
        this.#setReviewsIdle();

        this.#bindCardInteractions(strings);

        if (layout === "carousel") {
          const root = this.closest("[data-loox-sfr-section]");
          if (root) this.#bindCarousel(root);
        }

        if (enableLoadMore && data.pagination?.hasMore && data.pagination?.nextPageUrl) {
          const btn = this.querySelector(".loox-sfr__load-more");
          let nextUrl = resolveLooxUrl(data.pagination.nextPageUrl);
          const grid =
            layout === "carousel"
              ? this.querySelector(".loox-sfr__carousel-track")
              : this.querySelector(".loox-sfr__grid");

          btn?.addEventListener("click", async () => {
            if (!nextUrl || !grid || !(btn instanceof HTMLButtonElement)) return;
            btn.disabled = true;
            try {
              const nextRes = await fetch(nextUrl, fetchOpts);
              if (!nextRes.ok) {
                btn.disabled = false;
                return;
              }
              const nextData = await nextRes.json();
              const nextRaw = extractReviews(nextData);
              let merged = sortAndFilterReviews([...sorted, ...nextRaw], allowedRatings);
              await enrichReviewersFromDetail(merged, publicStoreId, locale, fetchOpts, productId);
              await enrichReviewsMediaFromDetail(merged, publicStoreId, productId, locale, fetchOpts);
              merged = sortAndFilterReviews(merged, allowedRatings);
              sorted = merged;
              this.#indexReviews(sorted);
              grid.innerHTML = sorted.map((r, i) => renderReviewCard(r, strings, i)).join("");
              this.#bindCardInteractions(strings);
              if (layout === "carousel") {
                const root = this.closest("[data-loox-sfr-section]");
                if (root) this.#bindCarousel(root);
              }

              if (!nextData.pagination?.hasMore || !nextData.pagination?.nextPageUrl) {
                btn.closest(".loox-sfr__load-more-wrap")?.remove();
              } else {
                nextUrl = resolveLooxUrl(nextData.pagination.nextPageUrl);
                btn.disabled = false;
              }
            } catch {
              btn.disabled = false;
            }
          });
        }
        return;
      }

      /** @type {unknown[]} */
      let combinedRaw = [];

      if (scope === "store" || scope === "both") {
        const storeUrl = new URL(`/storefront/v1/store/${publicStoreId}/store-reviews`, API_ORIGIN);
        storeUrl.searchParams.set("limit", String(limit));
        storeUrl.searchParams.set("sort", sort);
        storeUrl.searchParams.set("direction", direction);
        if (ratingsQueryValue) storeUrl.searchParams.set(LOOX_PRODUCT_REVIEWS_RATING_PARAM, ratingsQueryValue);
        if (locale) storeUrl.searchParams.set("locale", locale);

        const storeRes = await fetch(storeUrl.toString(), fetchOpts);
        if (!storeRes.ok) {
          let detail = "";
          try {
            const errBody = await storeRes.clone().json();
            if (errBody && typeof errBody === "object" && errBody.message) detail = String(errBody.message);
          } catch {
            try {
              detail = (await storeRes.clone().text()).slice(0, 200);
            } catch {
              /* ignore */
            }
          }
          this.#renderFetchError(storeRes.status, detail, strings, isDesignMode);
          return;
        }
        /** @type {Record<string, unknown>} */
        let storeData = await storeRes.json();
        let storeReviews = extractReviews(storeData);
        if (storeReviews.length === 0 && locale) {
          const retryUrl = new URL(storeUrl.toString());
          retryUrl.searchParams.delete("locale");
          const retryRes = await fetch(retryUrl.toString(), fetchOpts);
          if (retryRes.ok) {
            storeData = await retryRes.json();
            storeReviews = extractReviews(storeData);
          }
        }
        for (const row of storeReviews) {
          combinedRaw.push(tagReviewOrigin(row, "store"));
        }
      }

      if (scope === "product" || scope === "both") {
        /** @type {string[]} */
        const ids = [];
        if (primaryProductId) ids.push(primaryProductId);
        else {
          for (const part of productIdsRaw.split(",")) {
            const id = part.trim();
            if (id && !ids.includes(id)) ids.push(id);
          }
        }
        const cappedIds = ids.slice(0, 12);
        const perProductLimit = Math.min(
          50,
          Math.max(1, Math.ceil(limit / Math.max(1, cappedIds.length)) + 2)
        );

        for (const pid of cappedIds) {
          const pu = new URL(`/storefront/v1/store/${publicStoreId}/product-reviews`, API_ORIGIN);
          pu.searchParams.set("product_id", pid);
          pu.searchParams.set("limit", String(perProductLimit));
          pu.searchParams.set("sort", sort);
          pu.searchParams.set("direction", direction);
          if (ratingsQueryValue) pu.searchParams.set(LOOX_PRODUCT_REVIEWS_RATING_PARAM, ratingsQueryValue);
          if (locale) pu.searchParams.set("locale", locale);

          let pres = await fetch(pu.toString(), fetchOpts);
          if (!pres.ok) continue;
          /** @type {Record<string, unknown>} */
          let pdata = await pres.json();
          let pr = extractReviews(pdata);
          if (pr.length === 0 && locale) {
            const retryUrl = new URL(pu.toString());
            retryUrl.searchParams.delete("locale");
            const retryRes = await fetch(retryUrl.toString(), fetchOpts);
            if (retryRes.ok) {
              pdata = await retryRes.json();
              pr = extractReviews(pdata);
            }
          }
          for (const row of pr) {
            combinedRaw.push(tagReviewOrigin(row, "product"));
          }
        }
      }

      let mergedRaw = dedupeMergedLooxReviews(combinedRaw);
      /** @type {object[]} */
      let sortedMerged = sortAndFilterReviews(mergedRaw, allowedRatings);

      await enrichReviewersFromDetail(sortedMerged, publicStoreId, locale, fetchOpts, primaryProductId);
      await enrichReviewsMediaFromDetail(sortedMerged, publicStoreId, primaryProductId, locale, fetchOpts);
      sortedMerged = sortAndFilterReviews(sortedMerged, allowedRatings);
      sortedMerged = sortedMerged.slice(0, limit);

      let bottomlineHtmlMerged = "";
      if (showBottomline) {
        /** @type {{ averageRating?: number, totalReviews?: number } | null} */
        let blJson = null;
        if (primaryProductId && (scope === "product" || scope === "both")) {
          const blUrl = new URL(
            `/storefront/v1/store/${publicStoreId}/product-reviews/bottomline/${primaryProductId}`,
            API_ORIGIN
          );
          if (locale) blUrl.searchParams.set("locale", locale);
          const blRes = await fetch(blUrl.toString(), fetchOpts);
          if (blRes.ok) blJson = await blRes.json();
        }
        if (!blJson && (scope === "store" || scope === "both")) {
          const su = new URL(`/storefront/v1/store/${publicStoreId}/store-reviews/bottomline`, API_ORIGIN);
          if (locale) su.searchParams.set("locale", locale);
          const sr = await fetch(su.toString(), fetchOpts);
          if (sr.ok) blJson = await sr.json();
        }
        if (blJson && typeof blJson === "object") {
          const bl = /** @type {{ averageRating?: number, totalReviews?: number }} */ (blJson);
          const avg = bl.averageRating != null ? Number(bl.averageRating).toFixed(1) : "";
          const total = bl.totalReviews != null ? bl.totalReviews : "";
          if (avg !== "" || total !== "") {
            bottomlineHtmlMerged = `<div class="loox-sfr__bottomline"><span class="loox-sfr__bottomline-rating">${escapeHtml(String(avg))}</span><span class="loox-sfr__bottomline-sep">·</span><span class="loox-sfr__bottomline-count">${escapeHtml(String(total))} ${escapeHtml(reviewsLabel)}</span></div>`;
          }
        }
      }

      if (sortedMerged.length === 0) {
        this.#clearToolbarSummarySlot();
        const empty = this.dataset.emptyMessage?.trim();
        this.innerHTML = `<div class="loox-sfr__empty">${empty ? escapeHtml(empty).replace(/\n/g, "<br>") : ""}</div>`;
        this.#setReviewsIdle();
        return;
      }

      this.#indexReviews(sortedMerged);
      const cardsMerged = sortedMerged.map((r, i) => renderReviewCard(r, strings, i)).join("");
      const bodyMerged =
        layout === "carousel"
          ? `<div class="loox-sfr__carousel-viewport"><div class="loox-sfr__carousel-track">${cardsMerged}</div></div>`
          : `<div class="loox-sfr__grid">${cardsMerged}</div>`;

      this.#applyBottomlinePlacement(layout, bottomlineHtmlMerged, bodyMerged);
      this.#setReviewsIdle();

      this.#bindCardInteractions(strings);
      if (layout === "carousel") {
        const root = this.closest("[data-loox-sfr-section]");
        if (root) this.#bindCarousel(root);
      }
    } catch {
      this.#renderError(strings.error);
    }
  }

  /**
   * @param {number} status
   * @param {string} detail
   * @param {object} strings
   * @param {boolean} isDesignMode
   */
  #renderFetchError(status, detail, strings, isDesignMode) {
    let msg = strings.error || "Unable to load reviews.";
    if (status === 403 && strings.apiCors) {
      msg = strings.apiCors;
    } else if (status === 401) {
      msg = `${msg} Check your Loox Public Store ID in theme settings.`;
    } else if (detail && isDesignMode) {
      msg = `${msg} ${detail}`;
    } else if (status) {
      msg = `${msg} (${status})`;
    }
    this.#clearToolbarSummarySlot();
    this.innerHTML = `<div class="loox-sfr__error" role="alert">${escapeHtml(msg)}</div>`;
    this.#setReviewsIdle();
  }

  #renderError(message) {
    this.#clearToolbarSummarySlot();
    this.innerHTML = `<div class="loox-sfr__error" role="alert">${escapeHtml(message)}</div>`;
    this.#setReviewsIdle();
  }

  #renderMissingConfig() {
    this.#clearToolbarSummarySlot();
    const isDesignMode = Boolean(window.Shopify?.designMode);
    if (isDesignMode) {
      this.innerHTML =
        '<div class="loox-sfr__editor-hint">Add your Loox Public Store ID under Theme settings → Loox.</div>';
    } else {
      this.innerHTML = "";
    }
    this.#setReviewsIdle();
  }
}

if (!customElements.get("loox-storefront-reviews")) {
  customElements.define("loox-storefront-reviews", LooxStorefrontReviews);
}
